import { useEffect, useState, type ReactNode } from 'react';

import useInvestFunds from '../hooks/fund/useInvestFunds';
import LAppOnline from '../../../app/layouts/LAppOnline';
import InvestorContactDrawer from '../features/contact-drawer/InvestorContactDrawer';
import { useTranslation } from 'react-i18next';
import VMiscPlayerVideoTuto from '../../misc/views/VMiscPlayerVideoTuto';
import useCompanies from '../../user/hooks/useUserCompanys';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import { useAppSettings } from '../../../app/features/settings/providers/AppSettingsProvider';
import { useAppLang } from '../../../app/features/lang/providers/AppLangProvider';
import { useNavigate } from 'react-router-dom';
import useUserProfile from '../../user/hooks/useUserProfile';
import HAppRoutes from '../../../app/features/routes/helpers/HAppRoutes';
import AdminRoutes from '../../admin/AdminRoutes';
import FAppError from '../../../app/features/error/FAppError';
import AppLoaderContent from '../../../app/features/loader/content/AppLoaderContent';
import AppErrorBoundary from '../../../app/features/error/boundary/AppErrorBoundary';
import useUserMenu from '../../user/hooks/useUserMenu';
import { globalConfig } from '../../../app/config/config';
import FInvestSelectCurrencySwitch from '../features/select-currency/FInvestSelectCurrencySwitch';

type LInvestorProps = {
	children: ReactNode;
};

const LInvestor = (props: LInvestorProps) => {
	const auth = useAuthContext();
	const appSettings = useAppSettings();
	const appLang = useAppLang();
	const navigate = useNavigate();

	const userInvestorMenu = useUserMenu();

	const { t, i18n } = useTranslation();

	const { companies } = useCompanies();
	const { funds } = useInvestFunds();
	const { investorProfile } = useUserProfile(auth?.currentCompany?.id ? [auth.currentCompany.id] : undefined);

	const [url, setUrl] = useState<string | null>(null);
	useEffect(() => {
		if (i18n) {
			if (i18n.language === 'fr' && globalConfig.get().APP_VIDEO_TUTO_URL_FR !== '') {
				setUrl(globalConfig.get().APP_VIDEO_TUTO_URL_FR);
			} else if (i18n.language === 'en' && globalConfig.get().APP_VIDEO_TUTO_URL_EN !== '') {
				setUrl(globalConfig.get().APP_VIDEO_TUTO_URL_EN);
			} else {
				setUrl(null);
			}
		}
	}, [i18n, t]);

	useEffect(() => {
		const currentCompanyId = auth.currentCompany?.id;
		if (companies && (currentCompanyId === undefined || !companies.some((x) => x.id === currentCompanyId))) {
			// Init default currentCompany
			auth.setCurrentCompany(companies[0]);
		}
	}, [auth, companies]);

	// FEATURE LANG/STATUS/ROUNDING : UPDATE LANG, REALIZATION STATUS AND ROUNDING IF CHANGE IN PROFILE EDIT
	useEffect(() => {
		if (investorProfile && appSettings && appLang) {
			// Set settings of the current user from the Investor Profile
			if (investorProfile.defaultRounding && appSettings.rounding !== investorProfile.defaultRounding) {
				appSettings.changeRounding(investorProfile.defaultRounding);
			}
			if (investorProfile.defaultLanguage && appLang.language !== investorProfile.defaultLanguage) {
				appLang.changeLanguage(investorProfile.defaultLanguage);
			}
			if (
				investorProfile.realizationStatus &&
				appSettings.realizationStatus !== investorProfile.realizationStatus
			) {
				appSettings.changeRealizationStatus(investorProfile.realizationStatus);
			}
		}
	}, [appSettings, investorProfile, appLang]);

	useEffect(() => {
		if (auth) {
			// Redirection vers le dashboard admin si on est employé et que l'on a pas de overrideUserId
			if (auth.authContext === 'employee' && auth.authOverrideUserId === null) {
				navigate(HAppRoutes.getPath(AdminRoutes.dashboard.tag));
			}
		}
	}, [auth, navigate]);

	return (
		<LAppOnline
			navigationConfig={userInvestorMenu ?? undefined}
			bottomComponent={
				<>
					{url ? <VMiscPlayerVideoTuto /> : ''}
					<InvestorContactDrawer contacts={investorProfile ? investorProfile.contacts ?? [] : []} />
				</>
			}
			// FInvestSelectCurrencySwitch self-gates on displayCurrencySelector + funds count,
			// so it appears only on routes that opt in via setDisplayCurrencySelector(true).
			titleComponent={<FInvestSelectCurrencySwitch funds={funds ?? []} />}
		>
			{investorProfile !== undefined && investorProfile === null ? (
				<FAppError
					variant="container"
					title="No account found"
					content="Sorry but your account is not found, please contact your advisor"
				/>
			) : funds !== undefined && funds === null ? (
				<FAppError
					variant="container"
					title="No fund found"
					content="Sorry but you have no fund yet, please contact your advisor"
				/>
			) : companies !== undefined && (companies === null || companies.length === 0) ? (
				<FAppError
					variant="container"
					title="No company found"
					content="Sorry but you have no company yet, please contact your advisor"
				/>
			) : investorProfile === undefined || funds === undefined || companies === undefined ? (
				<AppLoaderContent />
			) : (
				<AppErrorBoundary>{props.children}</AppErrorBoundary>
			)}
		</LAppOnline>
	);
};

export default LInvestor;
